import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import {
  loadBattleQuestions,
  startBattleSession
} from "../battle/data/battleApi";
import { CampaignLevelPicker } from "../campaign/CampaignLevelPicker";
import { ReplayLevelDialog } from "../campaign/ReplayLevelDialog";
import {
  getCampaignLevelState,
  getInitialCampaignLevel
} from "../campaign/campaignProgress";
import type {
  BattleConfiguration,
  BattleQuestion
} from "../battle/domain/battleMachine";
import type {
  Database,
  CampaignLevelRow,
  HeroRow,
  Json,
  ProfileRow,
  SubjectRow,
  UserHeroRow,
  UserCampaignProgressRow,
  UserProgressRow
} from "../../shared/types/database";
import { getHeroLevelProgress } from "../../shared/lib/heroProgression";
import { rememberReturningHero } from "../auth/returningHero";

export interface BattleSetup {
  configuration: BattleConfiguration;
  difficulty: number;
  campaignLevel: number;
  hero: HeroRow;
  isReplay: boolean;
  opponent: {
    attackStyle: "melee" | "ranged" | "magic";
    isBoss: boolean;
    key: string;
    nameRu: string;
    nameUz: string;
    tier: number;
    variant: number;
  };
  playerName: string;
  questions: BattleQuestion[];
  sessionId: string;
  subject: SubjectRow;
}

interface LobbyScreenProps {
  client: SupabaseClient<Database>;
  onLogout: () => void;
  session: Session;
  onStart: (setup: BattleSetup) => void;
}

type UpgradeStat = "crit" | "attack" | "vitality" | "special";

export function LobbyScreen({ client, onLogout, session, onStart }: LobbyScreenProps) {
  const { i18n, t } = useTranslation();
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [heroes, setHeroes] = useState<HeroRow[]>([]);
  const [ownedHeroes, setOwnedHeroes] = useState<UserHeroRow[]>([]);
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);
  const [progress, setProgress] = useState<UserProgressRow[]>([]);
  const [campaignLevels, setCampaignLevels] = useState<CampaignLevelRow[]>([]);
  const [campaignProgress, setCampaignProgress] = useState<UserCampaignProgressRow | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [selectedHeroId, setSelectedHeroId] = useState<string | null>(null);
  const [selectedCampaignLevel, setSelectedCampaignLevel] = useState(1);
  const [replayConfirmationLevel, setReplayConfirmationLevel] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [upgrading, setUpgrading] = useState<UpgradeStat | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      const [
        profileResult,
        heroesResult,
        subjectsResult,
        ownedResult,
        progressResult,
        campaignLevelsResult,
        campaignProgressResult
      ] = await Promise.all([
        client.from("profiles").select("*").eq("id", session.user.id).maybeSingle(),
        client.from("heroes").select("*").eq("is_active", true).order("created_at"),
        client.from("subjects").select("*").eq("is_active", true).order("sort_order"),
        client.from("user_heroes").select("*").eq("user_id", session.user.id),
        client.from("user_progress").select("*").eq("user_id", session.user.id),
        client.from("campaign_levels").select("*").order("level_no"),
        client.from("user_campaign_progress").select("*").eq("user_id", session.user.id).maybeSingle()
      ]);

      if (!active) {
        return;
      }

      const firstError = profileResult.error
        ?? heroesResult.error
        ?? subjectsResult.error
        ?? ownedResult.error
        ?? progressResult.error
        ?? campaignLevelsResult.error
        ?? campaignProgressResult.error;
      if (firstError) {
        setError(t("lobbyLoadError"));
        setLoading(false);
        return;
      }

      const heroRows = heroesResult.data ?? [];
      const primaryHero = heroRows.find((hero) => hero.slug === "forest-knight")
        ?? heroRows[0];
      const availableHeroes = primaryHero ? [primaryHero] : [];
      const availableSubjects = subjectsResult.data ?? [];
      const owned = ownedResult.data ?? [];
      setProfile(profileResult.data);
      setHeroes(availableHeroes);
      setOwnedHeroes(owned);
      setSubjects(availableSubjects);
      setProgress(progressResult.data ?? []);
      setCampaignLevels(campaignLevelsResult.data ?? []);
      setCampaignProgress(campaignProgressResult.data);
      setSelectedCampaignLevel(getInitialCampaignLevel(
        campaignProgressResult.data?.current_level ?? 1
      ));
      setSelectedSubjectId(availableSubjects[0]?.id ?? null);
      setSelectedHeroId(availableHeroes[0]?.id ?? null);
      setLoading(false);
    };

    void load();
    return () => {
      active = false;
    };
  }, [client, session.user.id, t]);

  const selectedHero = useMemo(
    () => heroes.find((hero) => hero.id === selectedHeroId) ?? null,
    [heroes, selectedHeroId]
  );
  const selectedOwnedHero = useMemo(
    () => ownedHeroes.find((hero) => hero.hero_id === selectedHeroId) ?? null,
    [ownedHeroes, selectedHeroId]
  );
  const selectedSubject = useMemo(
    () => subjects.find((subject) => subject.id === selectedSubjectId) ?? null,
    [selectedSubjectId, subjects]
  );
  const subjectProgress = useMemo(
    () => progress.find((item) => item.subject_id === selectedSubjectId) ?? null,
    [progress, selectedSubjectId]
  );

  const isUzbek = i18n.resolvedLanguage?.startsWith("uz") ?? true;
  const metadataDisplayName = typeof session.user.user_metadata.display_name === "string"
    ? session.user.user_metadata.display_name
    : null;
  const displayName = profile?.display_name
    ?? metadataDisplayName
    ?? session.user.email?.split("@")[0]
    ?? t("playerFallbackName");
  const level = selectedOwnedHero?.level ?? 1;
  const heroProgress = getHeroLevelProgress(
    selectedOwnedHero?.experience ?? 0,
    level
  );
  const points = selectedOwnedHero?.upgrade_points ?? 0;

  useEffect(() => {
    if (!selectedHero || !session.user.email) {
      return;
    }

    rememberReturningHero({
      displayName,
      email: session.user.email,
      heroSlug: selectedHero.slug
    });
  }, [displayName, selectedHero, session.user.email]);

  const currentCampaignLevel = campaignProgress?.current_level ?? 1;
  const completedCampaignLevels = campaignProgress?.completed_levels ?? [];
  const selectedCampaign = useMemo(
    () => campaignLevels.find((item) => item.level_no === selectedCampaignLevel) ?? null,
    [campaignLevels, selectedCampaignLevel]
  );

  const launchBattle = async () => {
    if (!selectedHero || !selectedSubject) {
      return;
    }

    setStarting(true);
    setError(null);

    try {
      const started = await startBattleSession(
        client,
        selectedHero.id,
        selectedSubject.id,
        selectedCampaignLevel,
        isUzbek ? "uz" : "ru"
      );
      const questions = await loadBattleQuestions(
        client,
        started.battleSessionId,
        isUzbek ? "uz" : "ru"
      );

      onStart({
        configuration: {
          playerHp: started.playerHp,
          playerMaxHp: started.playerMaxHp,
          opponentHp: started.opponentHp,
          opponentMaxHp: started.opponentMaxHp,
          difficulty: started.difficulty
        },
        difficulty: started.difficulty,
        campaignLevel: started.campaignLevel,
        hero: selectedHero,
        isReplay: started.isReplay,
        opponent: {
          attackStyle: started.enemyAttackStyle,
          isBoss: started.isBoss,
          key: started.enemyKey,
          nameRu: started.enemyNameRu,
          nameUz: started.enemyNameUz,
          tier: started.enemyTier,
          variant: started.enemyVariant
        },
        playerName: displayName,
        questions,
        sessionId: started.battleSessionId,
        subject: selectedSubject
      });
    } catch {
      setError(t("lobbyStartError"));
      setStarting(false);
    }
  };

  const startBattle = () => {
    const state = getCampaignLevelState(
      selectedCampaignLevel,
      currentCampaignLevel,
      completedCampaignLevels
    );
    if (state === "completed") {
      setReplayConfirmationLevel(selectedCampaignLevel);
      return;
    }

    void launchBattle();
  };

  const upgrade = async (stat: UpgradeStat) => {
    if (!selectedHero || !selectedOwnedHero || points < 1 || upgrading) {
      return;
    }

    setUpgrading(stat);
    setError(null);
    const { data, error: upgradeError } = await client.rpc("upgrade_hero_stat", {
      p_hero_id: selectedHero.id,
      p_stat: stat
    });

    if (upgradeError || !isUpgradeResult(data)) {
      setError(t("lobbyUpgradeError"));
      setUpgrading(null);
      return;
    }

    setOwnedHeroes((current) => current.map((hero) => (
      hero.id === selectedOwnedHero.id
        ? {
          ...hero,
          upgrade_points: data.upgradePoints,
          crit_level: data.critLevel,
          attack_level: data.attackLevel,
          vitality_level: data.vitalityLevel,
          special_level: data.specialLevel
        }
        : hero
    )));
    setUpgrading(null);
  };

  if (loading) {
    return <OnboardingLoading />;
  }

  return (
    <main className="onboarding-shell onboarding-shell--lobby">
      <section className="lobby-panel" aria-labelledby="lobby-title">
        <header className="lobby-header">
          <div>
            <p>{t("lobbyWelcome")}</p>
            <h1 id="lobby-title">{displayName}</h1>
          </div>
          <div className="lobby-wallet" aria-label={t("lobbyCoins")}>
            <span aria-hidden="true">●</span>{profile?.coins ?? 0}
          </div>
          <button className="lobby-logout" type="button" onClick={onLogout}>
            {t("authLogout")}
          </button>
        </header>

        <div className="lobby-progress-card">
          <div>
            <strong>{t("heroLevel", { level })}</strong>
            <span>{t("winStreak", { count: subjectProgress?.current_streak ?? 0 })}</span>
          </div>
          <div
            className="lobby-xp-bar"
            aria-label={heroProgress.isMaxLevel
              ? t("maxHeroLevel")
              : t("heroXp", {
                current: heroProgress.xpIntoLevel,
                total: heroProgress.xpForNextLevel
              })}
          >
            <span style={{ width: `${heroProgress.progressPercent}%` }} />
          </div>
          <small>{heroProgress.isMaxLevel
            ? t("maxHeroLevel")
            : t("nextReward", { xp: heroProgress.xpUntilNextLevel })}</small>
        </div>

        <h2>{t("lobbyChooseSubject")}</h2>
        <div className="subject-picker">
          {subjects.map((subject) => (
            <button
              type="button"
              key={subject.id}
              aria-pressed={selectedSubjectId === subject.id}
              onClick={() => setSelectedSubjectId(subject.id)}
            >
              <span aria-hidden="true">{subjectIcon(subject.slug)}</span>
              {isUzbek ? subject.title_uz : subject.title_ru}
            </button>
          ))}
        </div>

        <h2>{t("lobbyChooseCampaignLevel")}</h2>
        <CampaignLevelPicker
          completedLevels={completedCampaignLevels}
          currentLevel={currentCampaignLevel}
          isUzbek={isUzbek}
          levels={campaignLevels}
          selectedLevel={selectedCampaignLevel}
          onSelect={(campaignLevel) => setSelectedCampaignLevel(campaignLevel.level_no)}
        />

        {selectedHero ? (
          <section className="upgrade-panel" aria-labelledby="upgrade-title">
            <header>
              <div>
                <small>{t("upgradePoints")}</small>
                <strong>{points}</strong>
              </div>
              <h2 id="upgrade-title">{t("upgradeHero")}</h2>
            </header>
            <div className="upgrade-grid">
              {(["attack", "vitality", "crit", "special"] as const).map((stat) => (
                <button
                  type="button"
                  key={stat}
                  disabled={!selectedOwnedHero || points < 1 || Boolean(upgrading)
                    || (selectedOwnedHero?.[`${stat}_level`] ?? 0) >= 10}
                  onClick={() => void upgrade(stat)}
                >
                  <span>{upgradeIcon(stat)}</span>
                  <strong>{t(`upgrade_${stat}`)}</strong>
                  <small>{t("upgradeLevel", {
                    level: selectedOwnedHero?.[`${stat}_level`] ?? 0
                  })}</small>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {error ? <p className="auth-message auth-message--error" role="alert">{error}</p> : null}
        <button
          className="primary-game-button"
          type="button"
          disabled={starting || !selectedHero || !selectedSubject || !selectedCampaign}
          onClick={startBattle}
        >
          {starting ? t("lobbyStarting") : t("lobbyStart")}
        </button>
        {replayConfirmationLevel !== null ? (
          <ReplayLevelDialog
            level={replayConfirmationLevel}
            onCancel={() => setReplayConfirmationLevel(null)}
            onConfirm={() => {
              setReplayConfirmationLevel(null);
              void launchBattle();
            }}
          />
        ) : null}
      </section>
    </main>
  );
}

interface UpgradeResult {
  upgradePoints: number;
  critLevel: number;
  attackLevel: number;
  vitalityLevel: number;
  specialLevel: number;
}

function isUpgradeResult(data: Json): data is Json & UpgradeResult {
  return typeof data === "object"
    && data !== null
    && !Array.isArray(data)
    && typeof data.upgradePoints === "number"
    && typeof data.critLevel === "number"
    && typeof data.attackLevel === "number"
    && typeof data.vitalityLevel === "number"
    && typeof data.specialLevel === "number";
}

function subjectIcon(slug: string) {
  return slug === "mathematics" ? "×"
    : slug === "english" ? "A"
      : slug === "geography" ? "◆"
        : "⌛";
}

function upgradeIcon(stat: UpgradeStat) {
  return stat === "attack" ? "⚔"
    : stat === "vitality" ? "♥"
      : stat === "crit" ? "✦"
        : "⚡";
}

function OnboardingLoading() {
  const { t } = useTranslation();
  return (
    <main className="onboarding-shell">
      <div className="onboarding-loading" role="status">
        <span />
        {t("authLoading")}
      </div>
    </main>
  );
}
